// Turnstile Widget Component
// Cloudflare Turnstile CAPTCHA integration

import { useCallback, useEffect, useRef, useState } from "react";
import {
  TURNSTILE_TIMEOUT_MESSAGE,
  formatTurnstileError,
  isIgnorableTurnstileError,
  isRetriableTurnstileError,
} from "@/lib/turnstileErrors";

interface TurnstileWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  className?: string;
}

type TurnstileRenderOptions = {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback"?: (error: string) => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  retry?: "auto" | "never";
  "refresh-expired"?: "auto" | "manual" | "never";
  theme?: "auto" | "light" | "dark";
};

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const MAX_AUTO_RETRIES = 3;
const TURNSTILE_SCRIPT_SELECTOR =
  'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]';

function getAutoRetryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 8000);
}

/**
 * Turnstile Widget.
 */
export function TurnstileWidget({
  siteKey,
  onVerify,
  onError,
  onExpire,
  className = "",
}: Readonly<TurnstileWidgetProps>) {
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const renderedSiteKeyRef = useRef<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const isRenderingRef = useRef(false);
  const autoRetryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRetriableErrorRef = useRef<string | null>(null);

  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;
  }, [onVerify, onError, onExpire]);

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const reportFinalError = useCallback((rawError: string) => {
    const message = formatTurnstileError(rawError);
    setError(message);
    setIsRetrying(false);
    onErrorRef.current?.(message);
  }, []);

  const scheduleAutoRetry = useCallback(
    (rawError: string) => {
      lastRetriableErrorRef.current = rawError;

      if (autoRetryCountRef.current >= MAX_AUTO_RETRIES) {
        reportFinalError(rawError);
        return;
      }

      autoRetryCountRef.current += 1;
      setError(null);
      setIsRetrying(true);

      clearRetryTimeout();
      const delayMs = getAutoRetryDelayMs(autoRetryCountRef.current - 1);
      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        setIsRetrying(false);

        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.reset(widgetIdRef.current);
            return;
          } catch (err) {
            console.error("Turnstile auto-retry reset failed:", err);
          }
        }

        reportFinalError(rawError);
      }, delayMs);
    },
    [clearRetryTimeout, reportFinalError]
  );

  const handleTurnstileError = useCallback(
    (rawError: string) => {
      if (isIgnorableTurnstileError(rawError)) {
        return;
      }

      if (isRetriableTurnstileError(rawError)) {
        scheduleAutoRetry(rawError);
        return;
      }

      reportFinalError(rawError);
    },
    [reportFinalError, scheduleAutoRetry]
  );

  const handleTurnstileTimeout = useCallback(() => {
    scheduleAutoRetry("Turnstile verification timed out");
  }, [scheduleAutoRetry]);

  const buildRenderOptions = useCallback((): TurnstileRenderOptions => {
    return {
      sitekey: siteKey,
      theme: "auto",
      retry: "auto",
      "refresh-expired": "auto",
      callback: (token: string) => {
        autoRetryCountRef.current = 0;
        lastRetriableErrorRef.current = null;
        setError(null);
        setIsRetrying(false);
        clearRetryTimeout();
        onVerifyRef.current(token);
      },
      "error-callback": handleTurnstileError,
      "expired-callback": () => {
        onExpireRef.current?.();
      },
      "timeout-callback": handleTurnstileTimeout,
    };
  }, [clearRetryTimeout, handleTurnstileError, handleTurnstileTimeout, siteKey]);

  const renderWidget = useCallback(() => {
    if (!widgetRef.current || !window.turnstile) {
      return false;
    }

    try {
      widgetIdRef.current = window.turnstile.render(widgetRef.current, buildRenderOptions());
      renderedSiteKeyRef.current = siteKey;
      isRenderingRef.current = true;
      return true;
    } catch (err) {
      console.error("Turnstile render failed:", err);
      reportFinalError("Failed to render Turnstile widget");
      isRenderingRef.current = false;
      renderedSiteKeyRef.current = null;
      return false;
    }
  }, [buildRenderOptions, reportFinalError, siteKey]);

  useEffect(() => {
    if (window.turnstile) {
      setScriptLoaded(true);
      return;
    }

    if (process.env.NODE_ENV === "development") {
      console.warn(
        "ℹ️ Turnstile Note: A 401 error from challenge-platform in Network tab is EXPECTED behavior.",
        "Turnstile checks PrivateToken support; the 401 is harmless and doesn't affect functionality."
      );
    }

    const existingScript = document.querySelector(TURNSTILE_SCRIPT_SELECTOR);
    if (existingScript) {
      setScriptLoaded(true);
      return;
    }

    // Official Cloudflare Turnstile CDN — no vendor SRI hashes (script rotates). HTTPS origin only.
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setScriptLoaded(true);
    };
    script.onerror = () => {
      reportFinalError("Failed to load Turnstile script");
    };
    document.body.appendChild(script);
  }, [reportFinalError]);

  useEffect(() => {
    if (isRenderingRef.current || !scriptLoaded || !widgetRef.current || !window.turnstile) {
      return;
    }

    if (widgetIdRef.current && renderedSiteKeyRef.current === siteKey) {
      return;
    }

    if (widgetIdRef.current && renderedSiteKeyRef.current !== siteKey) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (err) {
        console.error("Error removing old Turnstile widget:", err);
      }
      widgetIdRef.current = null;
      isRenderingRef.current = false;
    }

    renderWidget();

    return () => {
      clearRetryTimeout();
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.error("Error removing Turnstile widget:", err);
        }
        widgetIdRef.current = null;
        renderedSiteKeyRef.current = null;
        isRenderingRef.current = false;
      }
    };
  }, [clearRetryTimeout, renderWidget, scriptLoaded, siteKey]);

  /**
   * Reset.
   */
  const reset = () => {
    clearRetryTimeout();
    autoRetryCountRef.current = 0;
    lastRetriableErrorRef.current = null;
    setError(null);
    setIsRetrying(false);

    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        return;
      } catch (err) {
        console.error("Turnstile manual reset failed:", err);
      }
    }

    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (err) {
        console.error("Error removing Turnstile widget during reset:", err);
      }
    }

    widgetIdRef.current = null;
    renderedSiteKeyRef.current = null;
    isRenderingRef.current = false;

    setTimeout(() => {
      renderWidget();
    }, 100);
  };

  const statusMessage = isRetrying ? TURNSTILE_TIMEOUT_MESSAGE : error;

  return (
    <div className={className}>
      {statusMessage && (
        <div
          className={`mb-2 rounded border p-3 text-sm ${
            error
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
          role={error ? "alert" : "status"}
        >
          <p>{statusMessage}</p>
          {error && (
            <button
              type="button"
              onClick={reset}
              className="mt-2 text-sm font-medium text-red-600 underline hover:text-red-800"
              aria-label="Retry Turnstile verification"
            >
              Retry
            </button>
          )}
        </div>
      )}
      <div ref={widgetRef} aria-label="Cloudflare Turnstile verification" />
    </div>
  );
}
